import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { HeartPulse, ShieldAlert, Send, Copy, ChevronDown } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { Empty } from "./Common";

const expired = (s) => s?.due_at && s.due_at < new Date().toISOString();

export function ScreeningAlert({ client }) {
  const s = client?.screening;
  if (s?.flags?.length) {
    return (
      <div data-testid="screening-flags-box" className="rounded-[var(--radius)] border border-red-500/40 bg-red-500/10 p-4 text-red-900">
        <div className="flex items-center gap-2 font-semibold text-sm mb-2"><ShieldAlert className="h-4 w-4" />Health screening: {s.flags.length} item(s) to review{expired(s) && " · expired, ask for an update"}</div>
        <ul className="text-sm list-disc pl-5 space-y-0.5">{s.flags.map((f) => <li key={f}>{f}</li>)}</ul>
      </div>
    );
  }
  const text = !s ? "Health questionnaire not completed yet" : expired(s) ? "Health questionnaire is over 12 months old" : null;
  if (!text) return null;
  return <div data-testid="screening-missing-box" className="rounded-[var(--radius)] border border-dashed p-3 text-sm text-muted-foreground flex items-center gap-2"><HeartPulse className="h-4 w-4" />{text}</div>;
}

function Answer({ q, a }) {
  const v = q.type === "yesno" ? (a ? `${a.answer === "yes" ? "Yes" : "No"}${a.details ? ` — ${a.details}` : ""}` : "—")
    : q.type === "multi" ? (a?.length ? a.join(", ") : "—") : a || "—";
  const warn = q.flag && ((q.type === "yesno" && a?.answer === "yes") || (q.type === "multi" && a?.some((x) => !["None of these", "Nessuna di queste"].includes(x))));
  return <div className="grid sm:grid-cols-[1fr_1fr] gap-1 py-2 border-b last:border-0 text-sm"><dt className="text-muted-foreground">{q.label}</dt><dd className={warn ? "font-semibold text-red-700" : ""}>{v}</dd></div>;
}

function FormRow({ f, questions }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-[var(--radius)] border bg-card" data-testid={`health-form-${f.id}`}>
      <button className="w-full flex flex-wrap items-center gap-3 p-4 text-left text-sm" onClick={() => setOpen(!open)} disabled={!f.answers}>
        <span className="font-mono w-28">{(f.completed_at || f.created_at).slice(0, 10)}</span>
        <span className={`rounded-full px-2.5 py-1 text-xs ${f.status === "completed" ? "bg-emerald-500/15 text-emerald-800" : "bg-amber-500/15 text-amber-800"}`}>{f.status}</span>
        <span className="flex-1 text-muted-foreground">via {f.source}{f.flags?.length ? ` · ${f.flags.length} flagged` : ""}</span>
        {f.answers && <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && <dl className="px-4 pb-4">{questions.map((q) => <Answer key={q.key} q={q} a={f.answers[q.key]} />)}
        <p className="text-xs text-muted-foreground pt-2">Confirmed by {f.signer_name}</p></dl>}
    </div>
  );
}

export function HealthScreening({ client, onChanged }) {
  const { can } = useApp();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/clients/${client.id}/health`).then((r) => setD(r.data)), [client.id]);
  useEffect(() => { load(); }, [load]);
  const request = async () => {
    try {
      const { data } = await api.post(`/clients/${client.id}/health/request`);
      await navigator.clipboard?.writeText(data.url).catch(() => {});
      toast.success(data.emailed ? "Questionnaire emailed to the patient (link copied)" : "Link copied — send it to the patient");
      load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const s = d.screening;
  const pending = d.forms.find((f) => f.status === "pending");
  return (
    <div className="space-y-4" data-testid="health-screening">
      <div className="rounded-[var(--radius)] border bg-card p-4 flex flex-wrap items-center gap-3 text-sm">
        <HeartPulse className="h-4 w-4 text-primary" />
        <span className="flex-1" data-testid="health-status">{s.completed_at ? `Last completed ${s.completed_at.slice(0, 10)} · valid until ${s.due_at.slice(0, 10)}${d.valid ? "" : " (expired)"}` : "Not completed yet"}</span>
        {pending && <Button size="sm" variant="ghost" data-testid="health-copy-link-btn" onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/health/${pending.token}`).then(() => toast.success("Link copied"))}><Copy className="h-3.5 w-3.5 mr-1" />Copy link</Button>}
        {can("clients.write") && <Button size="sm" className="rounded-full" data-testid="health-request-btn" onClick={request}><Send className="h-3.5 w-3.5 mr-1" />{pending ? "Resend questionnaire" : "Send questionnaire"}</Button>}
      </div>
      <ScreeningAlert client={{ screening: s.status ? s : null }} />
      {!d.forms.length ? <Empty text="No questionnaires yet" /> : d.forms.map((f) => <FormRow key={f.id} f={f} questions={d.questions} />)}
      {!d.questions.length && <p className="text-xs text-muted-foreground">Only practitioners can read the full answers.</p>}
    </div>
  );
}
